import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { usePermissions } from "../../hooks/usePermissions";
import UserManagementService from "../../services/UserManagementService";
import AccountTypeSelection from "../../components/UserManagement/AccountTypeSelection";
import RegistrationForm from "../../components/UserManagement/RegistrationForm";
import { IAccountType } from "../../interfaces/userManagement.interface";
import { showToast } from "../../components/Toaster";
import logo from "../../assets/st-logo.svg";
import "./EnhancedSignUp.scss";

const UserOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { canCreateUser } = usePermissions();

  const [step, setStep] = useState(1);
  const [selectedAccountType, setSelectedAccountType] =
    useState<IAccountType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get available account types based on current user's permissions
  const getAvailableAccountTypes = (): IAccountType[] => {
    // If user is not logged in (self-registration), show all types
    if (!userProfile) {
      return UserManagementService.getAllAccountTypes();
    }

    // If user is logged in, get account types based on their permissions
    return UserManagementService.getAvailableAccountTypes(userProfile);
  };

  const handleAccountTypeSelect = (accountType: IAccountType) => {
    setSelectedAccountType(accountType);
  };

  const handleNext = () => {
    if (selectedAccountType) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedAccountType(null);
    }
  };

  const handleRegistrationSuccess = (userData: any) => {
    showToast("Account created successfully!", "success");

    // If self-registration, redirect to sign-in
    if (!userProfile) {
      navigate("/sign-in");
    } else {
      // If admin creating user, redirect to user management
      navigate("/users");
    }
  };

  const accountTypes = getAvailableAccountTypes();

  // If no account types available (user doesn't have permissions), show message
  if (accountTypes.length === 0 && userProfile) {
    return (
      <div className="user-onboarding-page flex items-center justify-center h-screen ambient-bkg">
        <div className="card-container flex flex-col items-center justify-center">
          <div className="logo-container">
            <img src={logo} alt="Smart Transport Logo" />
          </div>
          <div className="form-container">
            <div className="header-cover text_align_center">
              <div className="header-text">No Permissions</div>
              <div className="sub-header-text">
                You don't have permission to create new accounts.
              </div>
            </div>
            <button
              className="primary_button"
              onClick={() => navigate("/jobs/all")}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-onboarding-page flex items-center justify-center h-screen ambient-bkg">
      <div className="card-container flex flex-col items-center justify-center">
        <div className="logo-container">
          <img src={logo} alt="Smart Transport Logo" />
        </div>

        <div className="form-container">
          <div className="header-cover text_align_center">
            <div className="header-text">
              {step === 1 ? "Choose Account Type" : "Complete Registration"}
            </div>
            <div className="sub-header-text">
              {step === 1
                ? "Select the type of account that best fits your organization's needs"
                : `Fill in the details to create your ${selectedAccountType?.title.toLowerCase()} account`}
            </div>
          </div>

          {/* Progress indicator */}
          <div className="progress-indicator">
            <div className={`progress-step ${step >= 1 ? "active" : ""}`}>
              <div className="step-number">1</div>
              <div className="step-label">Account Type</div>
            </div>
            <div className="progress-line"></div>
            <div className={`progress-step ${step >= 2 ? "active" : ""}`}>
              <div className="step-number">2</div>
              <div className="step-label">Details</div>
            </div>
          </div>

          {/* Step content */}
          <div className="step-content">
            {step === 1 && (
              <AccountTypeSelection
                accountTypes={accountTypes}
                selectedType={selectedAccountType}
                onSelect={handleAccountTypeSelect}
                onNext={handleNext}
                isLoading={isLoading}
              />
            )}

            {step === 2 && selectedAccountType && (
              <RegistrationForm
                accountType={selectedAccountType}
                onBack={handleBack}
                onSuccess={handleRegistrationSuccess}
                isSelfRegistration={!userProfile}
              />
            )}
          </div>

          {/* Continue button for step 1 */}
          {step === 1 && selectedAccountType && (
            <button
              className="primary_button mt-1"
              onClick={handleNext}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  Loading...
                </div>
              ) : (
                "Continue"
              )}
            </button>
          )}

          {/* Footer */}
          {!userProfile && step === 1 && (
            <div className="footer_msg">
              Already have an account? <a href="/sign-in">Sign In</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserOnboarding;
